import React, { useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { format } from 'date-fns'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    InteractionMode
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import zoomPlugin from 'chartjs-plugin-zoom'
import 'chartjs-adapter-date-fns'

// Chart.js 등록
ChartJS.register(
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    zoomPlugin
)

const BIOMETRIC_COLORS = {
    heartRate: '#dc2626',
    respiration: '#2563eb',
    steps: '#047857',
    falls: '#d97706'
} as const

const ZOOM_ANIMATION_DURATION_MS = 260
const DAY_MS = 24 * 60 * 60 * 1000
const MINUTE_MS = 60 * 1000
const MAX_ZOOM_OUT_FACTOR = 3

// 생체신호 데이터 인터페이스
export interface IBiometricChartItem {
    x: number
    y: number
    yMin?: number
    yMax?: number
}

interface IProps {
    className?: string
    onViewModeChange?: (viewMode: 'daily' | 'realtime') => void
    heartRateData?: IBiometricChartItem[]
    respirationData?: IBiometricChartItem[]
    stepData?: IBiometricChartItem[]
    fallData?: IBiometricChartItem[]
    realtimeHeartRate?: IBiometricChartItem[]
    realtimeRespiration?: IBiometricChartItem[]
    realtimeSteps?: IBiometricChartItem[]
    realtimeFalls?: IBiometricChartItem[]
}

const BiometricDashboard: React.FC<IProps> = ({
    className,
    onViewModeChange,
    heartRateData,
    respirationData,
    stepData,
    fallData,
    realtimeHeartRate,
    realtimeRespiration,
    realtimeSteps,
    realtimeFalls
}) => {
    const { t } = useTranslation();
    // 차트 참조들
    const heartRateChartRef = useRef<ChartJS<'line'>>(null)
    const respirationChartRef = useRef<ChartJS<'line'>>(null)
    const stepChartRef = useRef<ChartJS<'line'>>(null)
    const fallChartRef = useRef<ChartJS<'line'>>(null)

    // 데이터 모드 상태
    const [viewMode, setViewMode] = useState<'daily' | 'realtime'>('daily')
    
    // 줌/팬 상태 (모든 차트 동기화용)
    const [syncedZoom, setSyncedZoom] = useState<{
        min?: number
        max?: number
    }>({})

    const renderMetricTitle = useCallback((titleKey: string, colorClassName: string) => {
        return parse(t(titleKey).replace(/text-sub_g/g, colorClassName))
    }, [t])

    const shouldRejectTouchPan = useCallback((event: any) => {
        const pointerType = event?.pointerType ?? event?.srcEvent?.pointerType
        const sourceEventType = String(event?.srcEvent?.type ?? '')
        const isMouseEvent = pointerType === 'mouse' || sourceEventType.startsWith('mouse')

        if (isMouseEvent) return false

        const pointerCount = event?.pointers?.length ?? event?.srcEvent?.touches?.length ?? event?.srcEvent?.changedTouches?.length ?? 0
        const isTouchEvent = pointerType === 'touch' || sourceEventType.startsWith('touch') || !!event?.srcEvent?.touches || !!event?.srcEvent?.changedTouches

        if (!isTouchEvent || pointerCount >= 2) return false

        const deltaX = Math.abs(event?.deltaX ?? 0)
        const deltaY = Math.abs(event?.deltaY ?? 0)
        const isHorizontalPan = deltaX > deltaY * 1.25

        return !isHorizontalPan
    }, [])

    // 데이터 선택 및 변환 로직
    const processedData = useMemo(() => {
        const selectData = (dailyData?: IBiometricChartItem[], realtimeData?: IBiometricChartItem[]) => {
            const sourceData = viewMode === 'realtime' ? (realtimeData || []) : (dailyData || [])
            return sourceData.filter(item => 
                !isNaN(item.x) && item.x > 0 && 
                item.y != null && !isNaN(item.y)
            ).sort((a, b) => a.x - b.x).map(item => ({
                x: item.x * 1000, // Chart.js는 밀리초 단위
                y: item.y,
                yMin: item.yMin,
                yMax: item.yMax
            }))
        }

        // 낙상 데이터 전용 처리 함수 - 연속적인 낙상 이벤트를 그룹화
        const selectFallData = (dailyData?: IBiometricChartItem[], realtimeData?: IBiometricChartItem[]) => {
            const sourceData = viewMode === 'realtime' ? (realtimeData || []) : (dailyData || [])
            const filteredData = sourceData.filter(item => 
                !isNaN(item.x) && item.x > 0 && 
                item.y != null && !isNaN(item.y)
            ).sort((a, b) => a.x - b.x)

            if (filteredData.length === 0) return []

            // 연속적인 낙상 이벤트를 그룹화 (10초 이내의 연속 이벤트는 하나로 처리)
            const groupedFalls: IBiometricChartItem[] = []
            const GROUPING_THRESHOLD = 10 // 10초
            
            let currentGroup: IBiometricChartItem[] = []
            
            for (let i = 0; i < filteredData.length; i++) {
                const currentItem = filteredData[i]
                
                if (currentGroup.length === 0) {
                    // 첫 번째 아이템이면 그룹에 추가
                    currentGroup.push(currentItem)
                } else {
                    const lastItem = currentGroup[currentGroup.length - 1]
                    const timeDiff = currentItem.x - lastItem.x // 초 단위
                    
                    if (timeDiff <= GROUPING_THRESHOLD) {
                        // 10초 이내면 같은 그룹에 추가
                        currentGroup.push(currentItem)
                    } else {
                        // 10초 초과면 이전 그룹을 완료하고 새 그룹 시작
                        if (currentGroup.length > 0) {
                            // 그룹의 첫 번째 시간을 대표 시간으로 사용하고, y값은 합계
                            const groupSum = currentGroup.reduce((sum, item) => sum + (item.y || 0), 0)
                            groupedFalls.push({
                                x: currentGroup[0].x,
                                y: groupSum,
                                yMin: currentGroup[0].yMin,
                                yMax: currentGroup[0].yMax
                            })
                        }
                        currentGroup = [currentItem]
                    }
                }
            }
            
            // 마지막 그룹 처리
            if (currentGroup.length > 0) {
                const groupSum = currentGroup.reduce((sum, item) => sum + (item.y || 0), 0)
                groupedFalls.push({
                    x: currentGroup[0].x,
                    y: groupSum,
                    yMin: currentGroup[0].yMin,
                    yMax: currentGroup[0].yMax
                })
            }

            return groupedFalls.map(item => ({
                x: item.x * 1000, // Chart.js는 밀리초 단위
                y: item.y,
                yMin: item.yMin,
                yMax: item.yMax
            }))
        }

        // 심박수와 호흡수는 min/max/avg 분리
        const selectVitalData = (dailyData?: IBiometricChartItem[], realtimeData?: IBiometricChartItem[]) => {
            const sourceData = viewMode === 'realtime' ? (realtimeData || []) : (dailyData || [])
            const filteredData = sourceData.filter(item => 
                !isNaN(item.x) && item.x > 0 && 
                item.y != null && !isNaN(item.y)
            ).sort((a, b) => a.x - b.x)

            // 각 데이터 포인트에서 min/avg/max 값들을 논리적 순서로 정렬
            const normalizedData = filteredData.map(item => {
                let min = item.yMin ?? item.y;
                let avg = item.y;
                let max = item.yMax ?? item.y;
                
                // NaN 값 처리
                if (isNaN(min)) min = avg;
                if (isNaN(max)) max = avg;
                if (isNaN(avg)) avg = 0;
                
                // 논리적 순서 보장: min <= avg <= max
                const values = [min, avg, max].sort((a, b) => a - b);
                
                return {
                    x: item.x * 1000,
                    sortedMin: values[0],
                    sortedAvg: values[1], 
                    sortedMax: values[2]
                };
            });

            return {
                max: normalizedData.map(item => ({
                    x: item.x,
                    y: item.sortedMax
                })),
                avg: normalizedData.map(item => ({
                    x: item.x,
                    y: item.sortedAvg
                })),
                min: normalizedData.map(item => ({
                    x: item.x,
                    y: item.sortedMin
                }))
            }
        }

        return {
            heartRate: selectVitalData(heartRateData, realtimeHeartRate),
            respiration: selectVitalData(respirationData, realtimeRespiration),
            steps: selectData(stepData, realtimeSteps),
            falls: selectFallData(fallData, realtimeFalls)
        }
    }, [
        heartRateData, respirationData, stepData, fallData,
        realtimeHeartRate, realtimeRespiration, realtimeSteps, realtimeFalls,
        viewMode
    ])

    // 차트 동기화 함수
    const syncAllCharts = useCallback((newZoom: { min?: number, max?: number }, animate = false) => {
        setSyncedZoom(newZoom)
        
        const charts = [
            heartRateChartRef.current,
            respirationChartRef.current,
            stepChartRef.current,
            fallChartRef.current
        ]

        charts.forEach(chart => {
            if (chart && chart.options.scales?.x) {
                chart.options.scales.x.min = newZoom.min
                chart.options.scales.x.max = newZoom.max
                chart.update(animate ? 'default' : 'none')
            }
        })
    }, [])

    // 차트 라벨 추출 함수 (HTML 태그 제거 및 다국어 지원)
    const extractChartLabel = useCallback((htmlContent: string) => {
        if (!htmlContent) return '';
        const match = htmlContent.match(/<span[^>]*>([\s\S]*?)<\/span>/);
        if (match && match[1]) {
            return match[1].replace(/[\x00-\x1F\x7F]/g, "");
        }
        return htmlContent.replace(/<[^>]+>/g, '').trim();
    }, []);

    // 공통 차트 옵션 생성 함수
    const createChartOptions = useCallback((
        title: string, 
        color: string, 
        yAxisTitle: string,
        suggestedMin?: number,
        suggestedMax?: number
    ) => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: ZOOM_ANIMATION_DURATION_MS,
            easing: 'easeOutCubic' as const
        },
        interaction: {
            mode: 'index' as InteractionMode,
            intersect: false,
        },
        plugins: {
            title: {
                display: Boolean(title),
                text: title,
                color: color,
                font: {
                    size: 16,
                    weight: 'bold' as const
                }
            },
            legend: {
                display: yAxisTitle.includes('(bpm)') || yAxisTitle.includes('(rpm)'),
                position: 'top' as const,
                labels: {
                    usePointStyle: true,
                    padding: 15
                }
            },
            tooltip: {
                mode: 'index' as InteractionMode,
                intersect: false,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: 'rgb(75, 85, 99)',
                bodyColor: 'rgb(75, 85, 99)',
                borderColor: 'rgb(229, 231, 235)',
                borderWidth: 1,
                cornerRadius: 8,
                titleAlign: 'center' as const,
                bodyAlign: 'center' as const,
                titleFont: {
                    size: 12,
                    weight: 'normal' as const
                },
                bodyFont: {
                    size: 12,
                    weight: 'normal' as const
                },
                padding: 12,
                callbacks: {
                    title: (tooltipItems: any[]) => {
                        if (tooltipItems.length > 0) {
                            const date = new Date(tooltipItems[0].parsed.x)
                            return viewMode === 'realtime' 
                                ? format(date, 'HH:mm:ss')
                                : format(date, 'MM/dd')
                        }
                        return ''
                    },
                    label: (context: any) => {
                        // 기본 툴팁 형태로 단순화
                        const value = Math.floor(context.parsed.y || 0);
                        return `${context.dataset.label}: ${value}`;
                    }
                }
            },
            zoom: {
                limits: {
                    x: {
                        minRange: viewMode === 'daily' ? DAY_MS : MINUTE_MS
                    }
                },
                pan: {
                    enabled: true,
                    mode: 'x' as const,
                    onPanStart: (context: any) => {
                        if (shouldRejectTouchPan(context.event)) {
                            return false
                        }
                    },
                    onPan: (context: any) => {
                        const { min, max } = context.chart.scales.x
                        syncAllCharts({ min, max })
                    }
                },
                zoom: {
                    wheel: {
                        enabled: true,
                        modifierKey: 'ctrl' as const,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x' as const,
                    onZoom: (context: any) => {
                        const { min, max } = context.chart.scales.x
                        syncAllCharts({ min, max })
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'time' as const,
                time: {
                    unit: viewMode === 'daily' ? 'day' as const : 'minute' as const,
                    tooltipFormat: viewMode === 'realtime' ? 'HH:mm:ss' : 'MM/dd',
                    displayFormats: {
                        second: 'HH:mm:ss',
                        minute: viewMode === 'realtime' ? 'HH:mm' : 'MM/dd',
                        hour: viewMode === 'realtime' ? 'HH:mm' : 'MM/dd',
                        day: 'MM/dd'
                    }
                },
                ticks: {
                    maxRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: viewMode === 'realtime' ? 8 : 10,
                    callback: (value: string | number) => {
                        const timestamp = typeof value === 'number' ? value : Number(value)
                        const date = new Date(timestamp)

                        if (Number.isNaN(date.getTime())) return ''

                        return viewMode === 'realtime'
                            ? format(date, 'HH:mm')
                            : format(date, 'MM/dd')
                    }
                },
                title: {
                    display: true,
                    text: viewMode === 'realtime' ? t('users.dailyChart.label.realtime') : t('users.dailyChart.label.daily')
                },
                min: syncedZoom.min,
                max: syncedZoom.max
            },
            y: {
                title: {
                    display: true,
                    text: yAxisTitle
                },
                suggestedMin,
                suggestedMax
            }
        }
    }), [syncedZoom, viewMode, syncAllCharts, shouldRejectTouchPan, t])

    // 심박수/호흡수용 3라인 차트 데이터 생성 함수
    const createVitalChartData = useCallback((
        data: { max: { x: number, y: number }[], avg: { x: number, y: number }[], min: { x: number, y: number }[] }, 
        baseColor: string
    ) => ({
        datasets: [
            {
                label: t('users.dailyChart.line.max'),
                data: data.max,
                borderColor: baseColor,
                backgroundColor: baseColor + '20',
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4
            },
            {
                label: t('users.dailyChart.line.average'),
                data: data.avg,
                borderColor: baseColor + 'CC', // 약간 투명하게
                backgroundColor: baseColor + '15',
                borderWidth: 3, // 두껍게
                fill: false,
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4
            },
            {
                label: t('users.dailyChart.line.min'),
                data: data.min,
                borderColor: baseColor + '99', // 더 투명하게
                backgroundColor: baseColor + '10',
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4
            }
        ]
    }), [t])

    // 일반 차트 데이터 생성 함수 (걸음수, 낙상)
    const createChartData = useCallback((data: { x: number, y: number }[], color: string, label: string) => ({
        datasets: [
            {
                label,
                data,
                borderColor: color,
                backgroundColor: color + '20',
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4
            }
        ]
    }), [])

    const dataTimeBounds = useMemo(() => {
        const timestamps = [
            ...processedData.heartRate.avg.map(item => item.x),
            ...processedData.respiration.avg.map(item => item.x),
            ...processedData.steps.map(item => item.x),
            ...processedData.falls.map(item => item.x)
        ].filter(value => Number.isFinite(value))

        if (timestamps.length < 2) return null

        const min = Math.min(...timestamps)
        const max = Math.max(...timestamps)

        if (min === max) return null

        return { min, max }
    }, [processedData])

    // 통계 계산
    const statistics = useMemo(() => {
        const calcStats = (data: { x: number, y: number }[]) => {
            if (data.length === 0) return null
            const values = data.map(d => d.y).filter(v => !isNaN(v) && isFinite(v))
            if (values.length === 0) return null
            
            return {
                latest: values[values.length - 1] || 0,
                average: values.reduce((sum, v) => sum + v, 0) / values.length,
                max: Math.max(...values),
                min: Math.min(...values)
            }
        }

        const calcVitalStats = (data: { max: { x: number, y: number }[], avg: { x: number, y: number }[], min: { x: number, y: number }[] }) => {
            if (data.avg.length === 0) return null
            const avgValues = data.avg.map(d => d.y).filter(v => !isNaN(v) && isFinite(v))
            if (avgValues.length === 0) return null
            
            return {
                latest: avgValues[avgValues.length - 1] || 0,
                average: avgValues.reduce((sum, v) => sum + v, 0) / avgValues.length,
                max: Math.max(...data.max.map(d => d.y).filter(v => !isNaN(v) && isFinite(v))),
                min: Math.min(...data.min.map(d => d.y).filter(v => !isNaN(v) && isFinite(v)))
            }
        }

        // 낙상 전용 통계 계산 - 그룹화된 데이터의 특성을 고려
        const calcFallStats = (data: { x: number, y: number }[]) => {
            if (data.length === 0) return null
            const values = data.map(d => d.y).filter(v => !isNaN(v) && isFinite(v))
            if (values.length === 0) return null
            
            return {
                totalCount: values.reduce((sum, v) => sum + v, 0), // 전체 낙상 횟수 (모든 그룹의 합계)
                groupCount: data.length, // 그룹화된 낙상 이벤트 수
                latest: values[values.length - 1] || 0, // 최근 그룹의 낙상 횟수
                max: Math.max(...values), // 단일 그룹에서 최대 낙상 횟수
                min: Math.min(...values)  // 단일 그룹에서 최소 낙상 횟수
            }
        }

        return {
            heartRate: calcVitalStats(processedData.heartRate),
            respiration: calcVitalStats(processedData.respiration),
            steps: calcStats(processedData.steps),
            falls: calcFallStats(processedData.falls)
        }
    }, [processedData])

    // 줌 리셋 함수
    const resetZoom = useCallback(() => {
        setSyncedZoom({})
        const charts = [
            heartRateChartRef.current,
            respirationChartRef.current,
            stepChartRef.current,
            fallChartRef.current
        ]

        charts.forEach(chart => {
            if (chart && (chart as any).resetZoom) {
                (chart as any).resetZoom()
            }
        })
    }, [])

    const handleViewModeChange = useCallback((nextViewMode: 'daily' | 'realtime') => {
        if (nextViewMode === viewMode) return

        resetZoom()
        setViewMode(nextViewMode)
        onViewModeChange?.(nextViewMode)
    }, [onViewModeChange, resetZoom, viewMode])

    const applyZoom = useCallback((scale: number) => {
        if (!dataTimeBounds) return

        const fullMin = dataTimeBounds.min
        const fullMax = dataTimeBounds.max
        const fullRange = fullMax - fullMin
        const maxRange = fullRange * MAX_ZOOM_OUT_FACTOR
        const currentMin = syncedZoom.min ?? fullMin
        const currentMax = syncedZoom.max ?? fullMax
        const currentRange = Math.max(currentMax - currentMin, fullRange * 0.01)
        const nextRange = Math.min(maxRange, Math.max(fullRange * 0.01, currentRange * scale))
        const center = currentMin + currentRange / 2
        const fullCenter = fullMin + fullRange / 2
        const nextCenter = nextRange > fullRange ? fullCenter : center
        const nextMin = nextCenter - nextRange / 2
        const nextMax = nextCenter + nextRange / 2

        syncAllCharts({ min: nextMin, max: nextMax }, true)
    }, [dataTimeBounds, syncAllCharts, syncedZoom])

    const zoomIn = useCallback(() => {
        applyZoom(0.65)
    }, [applyZoom])

    const zoomOut = useCallback(() => {
        applyZoom(1.5)
    }, [applyZoom])

    return (
        <div className={`${className} biometric-dashboard`}>
            <header className="biometric-dashboard__header">
                <div className="biometric-dashboard__heading">
                    <span>HEALTH SIGNAL</span>
                    <h2>{t('V3.biometricDashboard.title')}</h2>
                    <p>{t('V3.biometricDashboard.description')}</p>
                </div>
                <div className="biometric-dashboard__mode" aria-label={t('users.biometric.rangeLabel')}>
                    <button
                        type="button"
                        onClick={() => handleViewModeChange('realtime')}
                        className={viewMode === 'realtime' ? 'is-active' : ''}
                    >
                        <i />
                        {t('common.realtimeView')} · {t('users.biometric.tenMinutes')}
                    </button>
                    <button
                        type="button"
                        onClick={() => handleViewModeChange('daily')}
                        className={viewMode === 'daily' ? 'is-active' : ''}
                    >
                        {t('common.dayView')}
                    </button>
                </div>
            </header>

            <div className="biometric-dashboard__metrics">
                <article className="biometric-metric biometric-metric--heart">
                    <div className="biometric-metric__label"><span>HR</span>{renderMetricTitle('users.detail.heartRate.title', '')}</div>
                    <strong>{statistics.heartRate ? Math.round(statistics.heartRate.latest) : '—'}<small>bpm</small></strong>
                    <p>{t('users.dailyChart.line.average')} {statistics.heartRate ? Math.round(statistics.heartRate.average) : '—'} bpm</p>
                </article>
                <article className="biometric-metric biometric-metric--respiration">
                    <div className="biometric-metric__label"><span>RR</span>{renderMetricTitle('users.detail.respiration.title', '')}</div>
                    <strong>{statistics.respiration ? Math.round(statistics.respiration.latest) : '—'}<small>rpm</small></strong>
                    <p>{t('users.dailyChart.line.average')} {statistics.respiration ? Math.round(statistics.respiration.average) : '—'} rpm</p>
                </article>
                <article className="biometric-metric biometric-metric--steps">
                    <div className="biometric-metric__label"><span>ST</span>{t('users.biometric.steps')}</div>
                    <strong>{statistics.steps ? Math.round(statistics.steps.latest).toLocaleString() : '—'}<small>{t('common.step')}</small></strong>
                    <p>{t('users.dailyChart.line.average')} {statistics.steps ? Math.round(statistics.steps.average).toLocaleString() : '—'}</p>
                </article>
                <article className="biometric-metric biometric-metric--falls">
                    <div className="biometric-metric__label"><span>FD</span>{t('notification.falldetection.title')}</div>
                    <strong>{statistics.falls?.totalCount ?? 0}<small>{t('common.count')}</small></strong>
                    <p>{t('V3.biometricDashboard.stats.events')} {statistics.falls?.groupCount ?? 0} {t('V3.biometricDashboard.stats.cases')}</p>
                </article>
            </div>

            <div className="biometric-dashboard__toolbar">
                <span>{t('users.biometric.syncHint')}</span>
                <div>
                    <button type="button" onClick={zoomIn} disabled={!dataTimeBounds} aria-label={t('common.zoomIn')}>
                        + {t('common.zoomIn')}
                    </button>
                    <button type="button" onClick={zoomOut} disabled={!dataTimeBounds} aria-label={t('common.zoomOut')}>
                        − {t('common.zoomOut')}
                    </button>
                    <button type="button" onClick={resetZoom} disabled={!dataTimeBounds} aria-label={t('common.reset')}>
                        ↺ {t('common.reset')}
                    </button>
                </div>
            </div>

            <div className="biometric-dashboard__charts">
                <article className="biometric-chart biometric-chart--heart">
                    <div className="biometric-chart__heading">
                        <div><span>HR</span><strong>{t('V3.biometricDashboard.chart.heartRate.title')}</strong></div>
                        <small>bpm · {t('users.dailyChart.line.max')} / {t('users.dailyChart.line.average')} / {t('users.dailyChart.line.min')}</small>
                    </div>
                    <div className="biometric-chart__canvas">
                        <Line
                            ref={heartRateChartRef}
                            data={createVitalChartData(processedData.heartRate, BIOMETRIC_COLORS.heartRate)}
                            options={createChartOptions('', BIOMETRIC_COLORS.heartRate, `${extractChartLabel(t('users.detail.heartRate.title'))} (bpm)`, 40, 160)}
                        />
                    </div>
                </article>

                <article className="biometric-chart biometric-chart--respiration">
                    <div className="biometric-chart__heading">
                        <div><span>RR</span><strong>{t('V3.biometricDashboard.chart.respiration.title')}</strong></div>
                        <small>rpm · {t('users.dailyChart.line.max')} / {t('users.dailyChart.line.average')} / {t('users.dailyChart.line.min')}</small>
                    </div>
                    <div className="biometric-chart__canvas">
                        <Line
                            ref={respirationChartRef}
                            data={createVitalChartData(processedData.respiration, BIOMETRIC_COLORS.respiration)}
                            options={createChartOptions('', BIOMETRIC_COLORS.respiration, `${extractChartLabel(t('users.detail.respiration.title'))} (rpm)`, 8, 30)}
                        />
                    </div>
                </article>

                <article className="biometric-chart biometric-chart--steps">
                    <div className="biometric-chart__heading">
                        <div><span>ST</span><strong>{t('V3.biometricDashboard.chart.steps.title')}</strong></div>
                        <small>{t('common.step')} · {t('users.biometric.activityTrend')}</small>
                    </div>
                    <div className="biometric-chart__canvas">
                        <Line
                            ref={stepChartRef}
                            data={createChartData(processedData.steps, BIOMETRIC_COLORS.steps, extractChartLabel(t('users.detail.step.title')))}
                            options={createChartOptions('', BIOMETRIC_COLORS.steps, extractChartLabel(t('users.detail.step.title')), 0)}
                        />
                    </div>
                </article>

                <article className="biometric-chart biometric-chart--falls">
                    <div className="biometric-chart__heading">
                        <div><span>FD</span><strong>{t('notification.falldetection.title')}</strong></div>
                        <small>{t('V3.biometricDashboard.chart.falls.label')} · {t('users.biometric.eventLog')}</small>
                    </div>
                    <div className="biometric-chart__canvas">
                        <Line
                            ref={fallChartRef}
                            data={createChartData(processedData.falls, BIOMETRIC_COLORS.falls, t('V3.biometricDashboard.chart.falls.label'))}
                            options={createChartOptions('', BIOMETRIC_COLORS.falls, t('V3.biometricDashboard.chart.falls.label'), 0)}
                        />
                    </div>
                </article>
            </div>

            <div className="biometric-dashboard__guide">
                <strong>{t('V3.biometricDashboard.guide.title')}</strong>
                <span>{t('V3.biometricDashboard.guide.zoom.description')}</span>
                <span>{t('V3.biometricDashboard.guide.pan.description')}</span>
                <span>{t('V3.biometricDashboard.guide.reset.description')}</span>
            </div>
        </div>
    )
}

export default BiometricDashboard
